import { Box, Typography } from '@mui/material';
import { DownloadIcon, UploadIcon } from 'lucide-react';
import { enqueueSnackbar } from '~/utils/notifications';
import { ChangeEvent, useCallback, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilValue } from 'recoil';
import WorkspaceLogo from '~/components/Logo/WorkspaceLogo';
import { useRefuseWhileViewerOpening } from '~/hooks/useRefuseWhileViewerOpening';
import useDocumentDragDetect from '~/hooks/useDocumentDragDetect';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import { readDirectoryEntries, readFileEntry } from '~/utils/io/files/fileTree';
import { MAX_FILES } from './LocalFilesTab';

/**
 * Traverse a FileSystemEntry tree (files + directories) and collect File objects.
 * Uses an explicit stack (DFS). Stops early when fileLimit is reached to prevent
 * excessive work on huge directory structures.
 */
async function traverseFileTree(
    root: FileSystemEntry,
    relativePath = '',
    fileLimit: number = MAX_FILES
): Promise<File[]> {
    const result: File[] = [];
    const stack: Array<{ entry: FileSystemEntry; path: string }> = [
        { entry: root, path: relativePath },
    ];

    while (stack.length && result.length < fileLimit) {
        const { entry, path } = stack.pop()!;

        if (entry.isFile) {
            try {
                const file = await readFileEntry(
                    entry as FileSystemFileEntry,
                    path
                );
                result.push(file);
            } catch (err) {
                console.error('Failed to read file', err, entry);
            }
            continue;
        }

        if (entry.isDirectory) {
            const dir = entry as FileSystemDirectoryEntry;
            let children: FileSystemEntry[] = [];
            try {
                children = await readDirectoryEntries(dir);
            } catch (err) {
                console.error('Failed to read directory', err, entry);
            }
            const nextPath = path + dir.name + '/';
            // Push children onto stack (depth-first)
            for (let i = 0; i < children.length; i++) {
                if (result.length >= fileLimit) {
                    break;
                }
                stack.push({ entry: children[i], path: nextPath });
            }
        }
    }

    if (result.length >= fileLimit) {
        enqueueSnackbar(
            `Import canceled: more than ${MAX_FILES} files detected. Maximum allowed is ${MAX_FILES}.`,
            { variant: 'error' }
        );
        throw new Error(`File limit of ${MAX_FILES} exceeded. Stopping.`);
    }

    return result;
}

async function getFilesFromDataTransferItems(
    items: DataTransferItemList
): Promise<File[]> {
    const promises = Array.from(items, (item) => {
        const entry = item.webkitGetAsEntry?.();
        return entry
            ? traverseFileTree(entry, '', MAX_FILES).catch((err) => {
                  console.error('Error in resolving file tree', err);
                  return [];
              })
            : Promise.resolve([]);
    });
    const results = await Promise.all(promises);
    return results.flat();
}

function FilesDraggedContent() {
    return (
        <Box display="flex" flexDirection="column" alignItems="center">
            <DownloadIcon size={48} />
            <Box mb={2} mt={1}>
                <Typography variant="h5" textAlign="center">
                    <FormattedMessage id="dicom.loadFiles.dragNDrop" />
                </Typography>
                <Typography
                    color={(theme) => theme.palette.text.secondary}
                    fontSize={14}
                    textAlign="center"
                    width={500}
                >
                    <FormattedMessage id="rtviewerDescription" />
                </Typography>
            </Box>
            <Typography textAlign="center">
                <FormattedMessage id="dragAndDropInstruction" />
            </Typography>
        </Box>
    );
}

function WaitingForFilesContent() {
    return (
        <Box display="flex" flexDirection="column" alignItems="center">
            <UploadIcon size={48} />
            <Box mb={2} mt={1}>
                <Typography variant="h5" textAlign="center">
                    <FormattedMessage id="dicom.loadFiles.waiting" />
                </Typography>
                <Typography
                    color={(theme) => theme.palette.text.secondary}
                    fontSize={14}
                    textAlign="center"
                    width={500}
                >
                    <FormattedMessage id="rtviewerDescription" />
                </Typography>
            </Box>
            <Typography textAlign="center">
                <FormattedMessage id="dragAndDropInstruction" />
            </Typography>
        </Box>
    );
}

export default function DNDArea() {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const folderInputRef = useRef<HTMLInputElement>(null);
    const [isDragOver, setIsDragOver] = useState<boolean>(false);
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const { initializeEnvironment } = useViewportEnvironment();
    const { processImportedFiles } = useProcessDicomFiles();
    const refuseWhileOpening = useRefuseWhileViewerOpening();

    const handleFileChange = useCallback(
        async (e: ChangeEvent<HTMLInputElement>) => {
            e.preventDefault();
            e.stopPropagation();
            if (refuseWhileOpening()) {
                // Clear so re-selecting the same files fires a change event.
                e.target.value = '';
                return;
            }
            if (e.target.files && e.target.files.length > 0) {
                const files = Array.from(e.target.files);
                const processedItems = await processImportedFiles(files);
                if (processedItems) {
                    initializeEnvironment(processedItems);
                }
            }
        },
        [refuseWhileOpening, processImportedFiles, initializeEnvironment]
    );

    const handleDrop = useCallback(
        async (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragOver(false);
            if (refuseWhileOpening()) {
                return;
            }

            let files: File[] = [];

            if (e.dataTransfer?.items && e.dataTransfer.items.length > 0) {
                files = await getFilesFromDataTransferItems(
                    e.dataTransfer.items
                );
            } else if (
                e.dataTransfer?.files &&
                e.dataTransfer.files.length > 0
            ) {
                files = Array.from(e.dataTransfer.files);
            }

            if (files.length > 0) {
                const processedItems = await processImportedFiles(files);
                if (processedItems) {
                    initializeEnvironment(processedItems);
                }
            }
        },
        [refuseWhileOpening, processImportedFiles, initializeEnvironment]
    );

    const handleDragLeave = useCallback((e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const dropArea = e.currentTarget as HTMLElement;
        const relatedTarget = e.relatedTarget as HTMLElement;
        if (!dropArea.contains(relatedTarget)) {
            setIsDragOver(false);
        }
    }, []);

    const handleDragOver = useCallback(() => {
        setIsDragOver(true);
    }, []);

    useDocumentDragDetect({
        onDrop: handleDrop,
        onDragLeave: handleDragLeave,
        onDragEnter: handleDragOver,
    });

    const isActive = isDragOver || selectedPatientId == null;

    return (
        <>
            <Box
                position="absolute"
                overflow="hidden"
                top={0}
                left={0}
                zIndex={isDragOver ? 9999 : 900}
                width="100%"
                height="100%"
                sx={{ pointerEvents: isActive ? 'auto' : 'none' }}
            >
                <Box visibility={isActive ? 'visible' : 'hidden'}>
                    <input
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        ref={fileInputRef}
                    />
                    <input
                        type="file"
                        multiple
                        // @ts-expect-error webkitdirectory is non-standard but supported
                        webkitdirectory="true"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        ref={folderInputRef}
                    />

                    <Box
                        position="absolute"
                        width="100%"
                        height="100%"
                        px={1}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        onClick={() => fileInputRef.current?.click()}
                        onContextMenu={(e) => {
                            e.preventDefault();
                            folderInputRef.current?.click();
                        }}
                    >
                        <Box position="relative" sx={{ pointerEvents: 'none' }}>
                            <Box
                                position="absolute"
                                top={-100}
                                display="flex"
                                justifyContent="center"
                                width="100%"
                            >
                                <WorkspaceLogo height={80} />
                            </Box>
                            <Box
                                p={6}
                                border={(theme) =>
                                    `1px dashed ${theme.palette.primary.main}`
                                }
                                borderRadius={1}
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                bgcolor={(theme) =>
                                    isDragOver
                                        ? theme.palette.primary.main
                                        : theme.palette.background.paper
                                }
                            >
                                {isDragOver ? (
                                    <FilesDraggedContent />
                                ) : (
                                    <WaitingForFilesContent />
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </>
    );
}
